import { useMutation } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ADD_USER_TO_NO_LIMIT_GROUP } from '@/apollo/schemas/customers/addUserToNoLimitGroup';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';

interface NoLimitButtonProps {
  customerId: string;
  customerName: string;
  refetch: () => void;
}

const NoLimitButton = ({
  customerId,
  customerName,
  refetch,
}: NoLimitButtonProps) => {
  const { toast } = useToast();
  const { session } = useAuth();
  const [open, setOpen] = useState(false);

  const [addToNoLimit, { loading }] = useMutation(ADD_USER_TO_NO_LIMIT_GROUP, {
    onCompleted: (data) => {
      const result = data?.addUserToNoLimitGroup;
      if (result?.status) {
        toast({
          variant: 'success',
          title: 'Added to No Limit group',
          description: `${customerName} has been added to the No Limit group.`,
        });
        refetch();
      } else {
        toast({
          variant: 'destructive',
          title: 'Action failed',
          description: result?.message ?? 'An unexpected error occurred.',
        });
      }
      setOpen(false);
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to add to No Limit group',
        description: error.message,
      });
    },
  });

  const handleConfirm = () => {
    addToNoLimit({
      variables: { customerId },
      context: {
        headers: { identity: session?.getIdToken().getJwtToken() },
      },
    });
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
        onClick={() => setOpen(true)}
      >
        noLimit
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>Add to No Limit Group</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will add{' '}
            <span className="font-medium text-foreground">{customerName}</span>{' '}
            to the No Limit group, granting them elevated investment limits.
          </p>
          <p className="text-sm font-medium text-violet-600">
            Are you sure you want to add this customer to the No Limit group?
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NoLimitButton;
